import React, { useState, useEffect, useCallback } from 'react';
import { statsAPI } from '../services/api';
import './DeferralCalendar.css';

const DeferralCalendar = ({ dateRange, onDateSelect }) => {
  const [calendarData, setCalendarData] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const fetchCalendarData = useCallback(async () => {
    try {
      // Fetch stats for the current month
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];

      const response = await statsAPI.getStats({ startDate, endDate });
      const byDate = response.data.data?.byDate || {};
      
      // Transform data for calendar
      const calendar = {};
      Object.entries(byDate).forEach(([date, data]) => {
        calendar[date] = {
          deferred: data.deferred || 0,
          total: (data.sent || 0) + (data.failed || 0) + (data.deferred || 0)
        };
      });
      
      setCalendarData(calendar);
    } catch (err) {
      console.error('Failed to fetch calendar data:', err);
    }
  }, [currentMonth]);

  useEffect(() => {
    fetchCalendarData();
  }, [dateRange, currentMonth, fetchCalendarData]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const handleDateClick = (day) => {
    if (!day) return;
    
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    setSelectedDate(dateStr);
    if (onDateSelect) {
      onDateSelect(new Date(year, month, day));
    }
  };

  const getDateKey = (day) => {
    if (!day) return null;
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const getDeferralIntensity = (deferred, total) => {
    if (!deferred || !total) return 0;
    const percentage = (deferred / total) * 100;
    if (percentage >= 50) return 4; // Very high
    if (percentage >= 30) return 3; // High
    if (percentage >= 10) return 2; // Medium
    return 1; // Low
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
    setSelectedDate(null);
  };

  const days = getDaysInMonth(currentMonth);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="deferral-calendar">
      <div className="calendar-header">
        <button className="calendar-nav-btn" onClick={() => navigateMonth(-1)}>‹</button>
        <h3>{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</h3>
        <button className="calendar-nav-btn" onClick={() => navigateMonth(1)}>›</button>
      </div>
      
      <div className="calendar-grid">
        {dayNames.map(day => (
          <div key={day} className="calendar-day-header">{day}</div>
        ))}
        
        {days.map((day, index) => {
          const dateKey = getDateKey(day);
          const dayData = dateKey ? calendarData[dateKey] : null;
          const deferred = dayData?.deferred || 0;
          const total = dayData?.total || 0;
          const intensity = getDeferralIntensity(deferred, total);
          const isSelected = selectedDate === dateKey;
          
          return (
            <div
              key={index}
              className={`calendar-day ${!day ? 'empty' : ''} ${isSelected ? 'selected' : ''} intensity-${intensity}`}
              onClick={() => handleDateClick(day)}
              title={day && dateKey ? `Deferred: ${deferred}, Total: ${total}` : ''}
            >
              {day && (
                <>
                  <span className="day-number">{day}</span>
                  {deferred > 0 && (
                    <span className="deferral-badge">{deferred}</span>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
      
      <div className="calendar-legend">
        <div className="legend-item">
          <div className="legend-color intensity-0"></div>
          <span>No deferrals</span>
        </div>
        <div className="legend-item">
          <div className="legend-color intensity-1"></div>
          <span>Low (&lt;10%)</span>
        </div>
        <div className="legend-item">
          <div className="legend-color intensity-2"></div>
          <span>Medium (10-30%)</span>
        </div>
        <div className="legend-item">
          <div className="legend-color intensity-3"></div>
          <span>High (30-50%)</span>
        </div>
        <div className="legend-item">
          <div className="legend-color intensity-4"></div>
          <span>Very High (&gt;50%)</span>
        </div>
      </div>
    </div>
  );
};

export default DeferralCalendar;

